// Eszköz-KATALÓGUS (Ilcsa #113) — a szerkeszthető kategóriák és típusok kliens-oldali elérése. Minden
// select (űrlapok, szűrők), címke (lista, badge) ebből a EGY lekérdezésből táplálkozik; a beépített i18n
// típus-/kategória-szótár (`assets.typeLabel.*`, `assets.categoryLabel.*`) csak TARTALÉK (a katalógus még
// nem töltött be, vagy a kulcs nincs benne — pl. régi, törölt típus).
import { type AssetCatalogSchema } from '@atlas/contracts';
import { type TFunction } from 'i18next';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { type z } from 'zod';

import { api } from '@/lib/api';

export type AssetCatalog = z.infer<typeof AssetCatalogSchema>;
export type AssetCategory = AssetCatalog['categories'][number];
export type AssetType = AssetCategory['types'][number];

/** A katalógus query-kulcsa (a kezelő-sáv írásai ezt érvénytelenítik). */
export const assetCatalogKey = ['asset-catalog'] as const;

/** A kétnyelvű név a UI nyelve szerint; az `en` hiányában a magyar. */
export function catalogText(
  name: { hu: string; en?: string | null | undefined },
  lang: string,
): string {
  const en = name.en?.trim();
  return lang.toLowerCase().startsWith('en') && en ? en : name.hu;
}

/** A katalógusból számolt kereső-táblák egy nyelvre. */
export interface AssetCatalogLookup {
  categories: readonly AssetCategory[];
  /** A típus megjelenített neve (katalógus → beépített szótár → a nyers kulcs). */
  typeName: (key: string, t: TFunction) => string;
  /** A kategória megjelenített neve (katalógus → beépített szótár → a nyers kulcs). */
  categoryName: (key: string, t: TFunction) => string;
  /** A típus kategóriájának kulcsa; ismeretlen típus → `null`. */
  categoryKeyOfType: (typeKey: string) => string | null;
  /** Szoftver-jellegű-e a típus (licenckulcs + lejárat) — a kategóriája jelölése szerint. */
  isSoftwareType: (typeKey: string) => boolean;
  /** Egy kategória típusai (sorrendben); ismeretlen kategória → üres. */
  typesOfCategory: (categoryKey: string) => readonly AssetType[];
}

/** Tiszta építő (a hook és a tesztek közös útja). */
export function buildAssetCatalogLookup(
  categories: readonly AssetCategory[],
  lang: string,
): AssetCatalogLookup {
  const typeByKey = new Map<string, { type: AssetType; category: AssetCategory }>();
  const categoryByKey = new Map<string, AssetCategory>();
  for (const c of categories) {
    categoryByKey.set(c.key, c);
    for (const ty of c.types) typeByKey.set(ty.key, { type: ty, category: c });
  }
  return {
    categories,
    typeName: (key, t) => {
      const hit = typeByKey.get(key);
      return hit
        ? catalogText(hit.type.name, lang)
        : t(`assets.typeLabel.${key}`, { defaultValue: key });
    },
    categoryName: (key, t) => {
      const hit = categoryByKey.get(key);
      return hit
        ? catalogText(hit.name, lang)
        : t(`assets.categoryLabel.${key}`, { defaultValue: key });
    },
    categoryKeyOfType: (typeKey) => typeByKey.get(typeKey)?.category.key ?? null,
    isSoftwareType: (typeKey) => typeByKey.get(typeKey)?.category.isSoftware ?? false,
    typesOfCategory: (categoryKey) => categoryByKey.get(categoryKey)?.types ?? [],
  };
}

/**
 * A katalógus + a kereső-táblák. `enabled=false` (pl. jog nélkül) → nem kérdez. A `lookup` mindig használható:
 * betöltés előtt üres katalógussal számol, a címkék a tartalék-szótárra esnek.
 */
export function useAssetCatalog(enabled = true): {
  lookup: AssetCatalogLookup;
  isLoading: boolean;
  isError: boolean;
} {
  const { i18n } = useTranslation();
  const q = api.assetCatalog.catalog.useQuery(
    assetCatalogKey,
    {},
    { queryKey: assetCatalogKey, enabled },
  );
  const categories = q.data?.status === 200 ? q.data.body.categories : undefined;
  const lang = i18n.language;
  const lookup = useMemo(() => buildAssetCatalogLookup(categories ?? [], lang), [categories, lang]);
  return {
    lookup,
    isLoading: q.isLoading,
    isError: q.isError,
  };
}
